import React from 'react';
import { connect } from 'react-redux';
import { Tooltip } from '@mantine/core';
import {
  ObjGraphsBubbleHtmlContent,
  ObjGraphsBubbleHtmlStyles,
  NavigationButton,
  NavigationContainer,
  TableContainer,
  LabelDiv,
} from './ObjGraphsBubbleHtmlCSS';
import { abbreviateName } from '../../../Utils/TextUtils';
import { formatNumber, isJSON } from '../../../Utils/Utils';
import { Avatar } from '../../Avatar/Avatar';

const labelColWidth = 75;
const columnWidth = 30;

class ObjGraphsBubbleHtml extends React.Component {
  static defaultProps = {
    displayTotalsX: false,
    displayTotalsY: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      currentPage: 0,
      columnsPerPage: 3,
      animation: null,
      direction: 'next',
    };
    this.containerRef = React.createRef();
  }

  componentDidMount() {
    this.updateColumnsPerPage();
    window.addEventListener('resize', this.updateColumnsPerPage);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateColumnsPerPage);
  }

  updateColumnsPerPage = () => {
    if (this.containerRef.current) {
      const availableWidth =
        this.containerRef.current.getBoundingClientRect().width - labelColWidth;

      const columnsPerPage = Math.floor(availableWidth / columnWidth);
      this.setState({ columnsPerPage: Math.max(columnsPerPage, 1) });
    }
  };

  changePage = (direction) => {
    const { currentPage } = this.state;
    const totalPages = Math.ceil(
      this.props.data.length / this.state.columnsPerPage
    );
    const newPage = direction === 'next' ? currentPage + 1 : currentPage - 1;

    if (newPage >= 0 && newPage < totalPages) {
      this.setState({ animation: 'out', direction });
      setTimeout(() => {
        this.setState({
          currentPage: newPage,
          animation: 'in',
        });
      }, 300);
    }
  };

  getDisplayValue = (rawVal, index) => {
    if (this.props?.profiles[rawVal]) {
      return (
        <Avatar
          key={`av_${rawVal}${index}`}
          objId={rawVal}
          ratio={0.7}
          marginTop={7}
        />
      );
    }
    const dateTrg = new Date(Number(rawVal));
    if (!isNaN(dateTrg)) {
      return `${dateTrg.getDate()}/${dateTrg.getMonth() + 1}`;
    }
    return abbreviateName(rawVal);
  };

  calculateTotals = (matrix, xses, yses) => {
    const rowTotals = new Array(xses.length).fill(0);
    const colTotals = new Array(yses.length).fill(0);
    let hasNonNumeric = false;

    for (let i = 0; i < xses.length; i++) {
      for (let j = 0; j < yses.length; j++) {
        const val = matrix[i][j];
        if (typeof val === 'number' && !isNaN(val)) {
          rowTotals[i] += val;
          colTotals[j] += val;
        } else {
          hasNonNumeric = true;
        }
      }
    }

    return { rowTotals, colTotals, hasNumericValues: !hasNonNumeric };
  };

  render() {
    const { currentPage, columnsPerPage, animation, direction } = this.state;
    const { displayTotalsX, displayTotalsY } = this.props;
    const startIndex = currentPage * columnsPerPage;
    const endIndex = startIndex + columnsPerPage;

    const formulaCat1 = this.props.formulaCat1;
    const formulaCat2 = this.props.formulaCat2;
    const optionsCat1 = this.props.optionsCat1;
    const optionsCat2 = this.props.optionsCat2;
    const refValue = this.props.refValue;
    const totalPages = Math.ceil(
      this.props.data.length / this.state.columnsPerPage
    );

    let arrayOptionsCat2;
    if (isJSON(optionsCat2)) {
      arrayOptionsCat2 = JSON.parse(optionsCat2);
    }

    let arrayOptionsCat1;
    if (isJSON(optionsCat1)) {
      arrayOptionsCat1 = JSON.parse(optionsCat1);
    }

    let xses = [
      ...new Set(this.props.data.map((item) => item[formulaCat1])),
    ].sort((a, b) => {
      if (arrayOptionsCat1) {
        const indexA = arrayOptionsCat1.findIndex((opt) => opt.value === a);
        const indexB = arrayOptionsCat1.findIndex((opt) => opt.value === b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB; // ordre défini
        if (indexA !== -1) return -1; // a est défini, b pas défini
        if (indexB !== -1) return 1; // b est défini, a pas défini
      }
      // fallback tri standard
      return typeof a === 'number' && typeof b === 'number'
        ? a - b
        : a.toString().localeCompare(b.toString());
    });

    let yses = [
      ...new Set(this.props.data.map((item) => item[formulaCat2])),
    ].sort((a, b) => {
      if (arrayOptionsCat2) {
        const indexA = arrayOptionsCat2.findIndex((opt) => opt.value === a);
        const indexB = arrayOptionsCat2.findIndex((opt) => opt.value === b);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
      }
      return typeof a === 'number' && typeof b === 'number'
        ? a - b
        : a.toString().localeCompare(b.toString());
    });

    const matrix = Array(xses.length)
      .fill()
      .map(() => Array(yses.length).fill(0));
    let maxAbsValue = 1;

    for (const item of this.props.data) {
      const xval = item[formulaCat1];
      const yval = item[formulaCat2];
      const resval = item[Object.keys(item)[0]];
      const matx = xses.indexOf(xval);
      const maty = yses.indexOf(yval);
      matrix[matx][maty] = (matrix[matx][maty] || 0) + Number(resval);
      maxAbsValue = Math.max(maxAbsValue, Math.abs(matrix[matx][maty]));
    }

    const { rowTotals, colTotals, hasNumericValues } = this.calculateTotals(
      matrix,
      xses,
      yses
    );
    const calcRefValue = refValue ?? maxAbsValue;
    const ratio = 30 / calcRefValue;

    const lines = [];

    if (displayTotalsY && hasNumericValues) {
      const totalHeader = [<td key="total-header">Totaux</td>];
      for (let j = startIndex; j < Math.min(endIndex, yses.length); j++) {
        totalHeader.push(
          <td key={`total-col-${j}`} style={ObjGraphsBubbleHtmlStyles.td}>
            <div style={ObjGraphsBubbleHtmlStyles.total}>
              {formatNumber(colTotals[j])}
            </div>
          </td>
        );
      }
      if (displayTotalsX) {
        totalHeader.push(<td key="total-header-spacer" />);
      }
      lines.push(<tr key="column-totals">{totalHeader}</tr>);
    }
    let colorToUseX = '#000000';
    for (let i = 0; i < xses.length; i++) {
      let valueToUse;
      let iconToUse;
      if (arrayOptionsCat1) {
        colorToUseX =
          arrayOptionsCat1?.find((elemIn) => elemIn.value === xses[i])?.color ??
          colorToUseX;
        valueToUse = arrayOptionsCat1?.find(
          (elemIn) => elemIn.value === xses[i]
        )?.display;
        iconToUse = arrayOptionsCat1?.find(
          (elemIn) => elemIn.value === xses[i]
        )?.icon;
      }

      const line = [
        <td
          key={`td${i}`}
          title={xses[i]}
          style={{ ...ObjGraphsBubbleHtmlStyles.xses }}
        >
          <LabelDiv key={`td${i}`} title={xses[i]} width={labelColWidth}>
            {iconToUse && (
              <div
                key={`icon-hd-${iconToUse}-${colorToUseX}`}
                style={{
                  flex: '0 0 auto',
                  width: '16px',
                  height: '16px',
                  backgroundColor: colorToUseX,
                  mask: `url('${iconToUse ?? '/img/alert.svg'}')`,
                  maskRepeat: 'no-repeat',
                  maskSize: '16px 16px',
                  opacity: '1',
                  color: 'transparent',
                  marginLeft: '0px',
                  marginRight: '2px',
                }}
              >
                &nbsp;
              </div>
            )}
            <Tooltip
              key={`tt_${i}`}
              label={
                this.props?.profiles[xses[i]]?.name ?? (valueToUse || xses[i])
              }
            >
              {valueToUse || this.getDisplayValue(xses[i], i)}
            </Tooltip>
          </LabelDiv>
        </td>,
      ];

      for (let j = startIndex; j < Math.min(endIndex, yses.length); j++) {
        const val = matrix[i][j];
        line.push(
          <td key={`tt_${i}${j}`} style={ObjGraphsBubbleHtmlStyles.td}>
            <div style={ObjGraphsBubbleHtmlStyles.bubbleBg}>
              {val !== 0 && (
                <ObjGraphsBubbleHtmlContent
                  $lineHeight={`${
                    ratio * Math.min(calcRefValue, Math.abs(val))
                  }px`}
                  $height={`${ratio * Math.min(calcRefValue, Math.abs(val))}px`}
                  $width={`${ratio * Math.min(calcRefValue, Math.abs(val))}px`}
                  $pal={this.props.pal}
                  $aboveRef={Math.abs(val) > calcRefValue}
                  $isNegative={val < 0}
                >
                  {isNaN(val) ? val : formatNumber(val)}
                </ObjGraphsBubbleHtmlContent>
              )}
            </div>
          </td>
        );
      }

      if (displayTotalsX && hasNumericValues) {
        line.push(
          <td key={`row-total-${i}`} style={ObjGraphsBubbleHtmlStyles.td}>
            <div style={ObjGraphsBubbleHtmlStyles.total}>
              {formatNumber(rowTotals[i])}
            </div>
          </td>
        );
      }

      lines.push(
        <tr style={{ width: '100%' }} key={`l_${i}`}>
          {line}
        </tr>
      );
    }

    const navigationRow = [
      <td key="td_navigContainr">
        <NavigationContainer $disabled={totalPages < 2}>
          <NavigationButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              this.changePage('prev');
            }}
            onMouseUp={(e) => e.stopPropagation()}
            disabled={currentPage === 0}
          >
            &lt;
          </NavigationButton>
          <NavigationButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              e.nativeEvent.stopImmediatePropagation();
              this.changePage('next');
            }}
            onMouseUp={(e) => e.stopPropagation()}
            disabled={endIndex >= yses.length}
          >
            &gt;
          </NavigationButton>
        </NavigationContainer>
      </td>,
    ];

    let colorToUse = '#000000';
    for (let j = startIndex; j < Math.min(endIndex, yses.length); j++) {
      let valueToUse;
      let iconToUse;
      if (arrayOptionsCat2) {
        colorToUse =
          arrayOptionsCat2?.find((elemIn) => elemIn.value === yses[j])?.color ??
          colorToUse;
        valueToUse = arrayOptionsCat2?.find(
          (elemIn) => elemIn.value === yses[j]
        )?.display;
        iconToUse = arrayOptionsCat2?.find(
          (elemIn) => elemIn.value === yses[j]
        )?.icon;
      }
      navigationRow.push(
        <td
          key={`td_${j}`}
          style={{ ...ObjGraphsBubbleHtmlStyles.date, color: colorToUse }}
        >
          <Tooltip key={`ttyses_${j}`} label={valueToUse}>
            {iconToUse ? (
              <div
                key={`icon-bubble-${iconToUse}-${colorToUse}`}
                style={{
                  width: '16px',
                  height: '16px',
                  backgroundColor: colorToUse,
                  mask: `url('${iconToUse ?? '/img/alert.svg'}')`,
                  maskRepeat: 'no-repeat',
                  maskSize: '16px 16px',
                  opacity: '1',
                  color: 'transparent',
                  display: 'inline-block',
                  verticalAlign: 'middle',
                }}
              >
                .
              </div>
            ) : (
              valueToUse || this.getDisplayValue(yses[j], j)
            )}
          </Tooltip>
        </td>
      );
    }

    if (displayTotalsX && hasNumericValues) {
      navigationRow.push(<td key="nav-total-spacer" />);
    }

    lines.push(
      <tr style={{ border: '0px solid red' }} key="lasttr">
        {navigationRow}
      </tr>
    );

    return (
      <div ref={this.containerRef} style={ObjGraphsBubbleHtmlStyles.content}>
        <div>
          <div style={ObjGraphsBubbleHtmlStyles.table}>
            <TableContainer $animation={animation} $direction={direction}>
              <tbody
                styles={{
                  width: '100%',
                  display: 'table-row-group',
                  border: '1px solid green',
                }}
              >
                {lines}
              </tbody>
            </TableContainer>
          </div>
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    profiles: state.objects.profiles,
  };
}

const mapDispatchToProps = (dispatch) => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ObjGraphsBubbleHtml);
